import { FC, ReactNode, Suspense, useMemo } from 'react';
import { Box, Divider, styled, Tab, Tabs } from '@mui/material';
import { TabContext, TabPanel } from '@mui/lab';
import {
  useGetSelectedTab,
  OptionalTabs,
  useSetSelectedTab,
} from '../services/flowManager';
import { addToContext } from '../services/contextManager';
import { AppView } from '../constants';
import { sendButtonClickEvent } from '../services/trackerManager';
import { Loader } from './Loader';

type TabsControlProps = {
  tabs: {
    [tabName: string]: {
      content: ReactNode;
      icons: {
        active: JSX.Element;
        inactive?: JSX.Element;
      };
    };
  };
  blockingContent?: {
    isBlocking: boolean;
    content: ReactNode;
  };
};

const StyledTabName = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
}));

const StyledDivider = styled(Divider)({
  position: 'absolute',
  height: 44,
  marginRight: 0,
  marginLeft: 0,
  right: 0,
});

const TabsControl: FC<TabsControlProps> = ({ tabs, blockingContent }) => {
  const tabsNames = Object.keys(tabs);
  const selectedTab = useGetSelectedTab();
  const updateSelectedTab = useSetSelectedTab();

  const selectedTabIndex = useMemo(
    () => tabsNames.indexOf(selectedTab),
    [selectedTab, tabsNames],
  );

  const handleChange = (newValue: OptionalTabs) => {
    sendButtonClickEvent({ buttonName: `${newValue}-tab` });
    updateSelectedTab(newValue);

    addToContext(
      'view',
      newValue === 'customers'
        ? AppView.Consumers
        : newValue === 'pending'
        ? AppView.Pending
        : newValue === 'transactions'
        ? AppView.Transactions
        : AppView.CreateCart,
    );
  };

  return (
    <TabContext value={selectedTab}>
      <Box>
        {!blockingContent?.isBlocking && (
          <Tabs
            value={selectedTab}
            onChange={(_, newValue) => handleChange(newValue)}
          >
            {tabsNames.map((tab, idx) => {
              const tabLabel = (
                <>
                  <StyledTabName>
                    <div>
                      {tab === selectedTab
                        ? tabs[tab].icons.active
                        : tabs[tab].icons.inactive ?? tabs[tab].icons.active}
                    </div>
                    <div>{tab}</div>
                  </StyledTabName>
                  {idx !== selectedTabIndex && idx !== selectedTabIndex - 1 && (
                    <StyledDivider orientation="vertical" />
                  )}
                </>
              );
              return (
                <Tab disableRipple key={tab} label={tabLabel} value={tab} />
              );
            })}
          </Tabs>
        )}
      </Box>
      <TabPanel value={selectedTab}>
        {blockingContent?.isBlocking ? (
          blockingContent.content
        ) : (
          <Suspense fallback={<Loader />}>{tabs[selectedTab].content}</Suspense>
        )}
      </TabPanel>
    </TabContext>
  );
};

export default TabsControl;
